/**
 * Глобальный helper "Add to project" для кнопки [data-add-to-project-btn].
 *
 * Регистрирует window.initAddToProjectButton — инициализатор кнопки в любом
 * контейнере (странице товара или модалке выбора). Конфиг передаётся через
 * window.productAddProjectConfig (i18n + csrfToken).
 *
 * Используется:
 *   - resources/views/includes/parts/product_add_project.blade.php
 *     (инициализирует кнопку на странице товара через window.userProjects);
 *   - resources/js/features/product-modal/index.js
 *     (инициализирует кнопку внутри fancybox-модалки выбора товара).
 */

function setupGlobalHelper() {
    const $ = window.jQuery || window.$;
    if (!$) {
        console.error('[product-add-project] jQuery not loaded');
        return;
    }

    // Конфиг должен быть выставлен общим layout (см. inc_footer.blade.php).
    const config = window.productAddProjectConfig || {};
    const i18n = Object.assign({
        addToProject: 'Add to the project',
        addToRequest: 'Add to the request',
        adding: 'Adding...',
        createNewProject: 'Create new project',
        newProjectName: 'New project name',
        create: 'Create',
        cancel: 'Cancel',
        selectProject: 'Select project',
        selectRoom: 'Select room',
        confirm: 'Confirm',
        add: 'Add',
        productAddedToRequest: 'Product added to request successfully',
        errorAddingProduct: 'Error adding product',
        errorOccurred: 'An error occurred',
        noRoomsAvailable: 'No rooms available. Product will be added without room.',
    }, config.i18n || {});
    const csrfToken = config.csrfToken || $('meta[name="csrf-token"]').attr('content');

    window.initAddToProjectButton = function (container, productId, projectsData, modalContext, contextData) {
        const context = container || document;
        const $button = $(context).find('[data-add-to-project-btn]');
        if (!$button.length) return;

        if (!productId) productId = $button.data('product-id');

        contextData = contextData || {};
        const projectId = contextData.projectId || null;
        const roomId = contextData.roomId || null;
        const requestId = contextData.requestId || null;
        const rooms = contextData.rooms || [];

        if (requestId) {
            $button.text(i18n.addToRequest);
        } else if (projectId) {
            $button.text(i18n.addToProject);
        }

        let enableButton = null;

        $button.off('click').on('click', function (e) {
            e.preventDefault();
            if ($button.prop('disabled') || $button.hasClass('is-loading')) return;

            $button.prop('disabled', true).addClass('is-loading');
            const originalText = $button.text();
            $button.html('<span class="spinner-border spinner-border-sm me-2"></span>' + i18n.adding);

            enableButton = function () {
                $button.prop('disabled', false).removeClass('is-loading').text(originalText);
            };

            const quantity = parseInt($(context).find('input[name="quantity"]').val(), 10) || 1;
            const sizeIndex = $(context).find('select[name="size_index"]').val() || 0;

            // Случай 1: запрос менеджера
            if (requestId) {
                if (roomId) {
                    addProductToRequest(productId, requestId, roomId, quantity, sizeIndex);
                } else if (rooms && rooms.length > 0) {
                    showRoomSelectModal(rooms).then(function (selectedRoomId) {
                        if (selectedRoomId) {
                            addProductToRequest(productId, requestId, selectedRoomId, quantity, sizeIndex);
                        } else {
                            enableButton();
                        }
                    });
                } else {
                    addProductToRequest(productId, requestId, null, quantity, sizeIndex);
                }
                return;
            }

            // Случай 2: проект и комната уже известны
            if (projectId && roomId) {
                addProductToProjectWithRoom(productId, projectId, roomId, quantity, sizeIndex);
                return;
            }

            // Случай 3: только projectId — спрашиваем комнату
            if (projectId) {
                if (rooms && rooms.length > 0) {
                    showRoomSelectModal(rooms).then(function (selectedRoomId) {
                        if (selectedRoomId) {
                            addProductToProjectWithRoom(productId, projectId, selectedRoomId, quantity, sizeIndex);
                        } else {
                            addProductToProjectWithRoom(productId, projectId, null, quantity, sizeIndex);
                        }
                    });
                } else {
                    addProductToProjectWithRoom(productId, projectId, null, quantity, sizeIndex);
                }
                return;
            }

            // Случай 4: ничего не известно — обычная логика выбора проекта
            if (!projectsData && window.userProjects) projectsData = window.userProjects;
            if (!projectsData) projectsData = [];

            const hasNewOption = projectsData.some(function (item) { return item.value === 'new'; });
            if (!hasNewOption) {
                projectsData.push({ value: 'new', text: '+ ' + i18n.createNewProject });
            }

            if (projectsData.length === 1) {
                window.showInputModal({
                    title: i18n.createNewProject,
                    placeholder: '',
                    confirmText: i18n.create,
                    cancelText: i18n.cancel,
                }).then(function (projectName) {
                    if (projectName) {
                        addProductToNewProject(productId, projectName, context);
                    } else {
                        enableButton();
                    }
                });
            } else {
                window.showSelectModal({
                    title: i18n.selectProject,
                    placeholder: '',
                    items: projectsData,
                    confirmText: i18n.add,
                    cancelText: i18n.cancel,
                }).then(function (selectedProjectId) {
                    if (selectedProjectId === 'new') {
                        window.showInputModal({
                            title: i18n.newProjectName,
                            placeholder: '',
                            confirmText: i18n.create,
                            cancelText: i18n.cancel,
                        }).then(function (projectName) {
                            if (projectName) {
                                addProductToNewProject(productId, projectName, context);
                            } else {
                                enableButton();
                            }
                        });
                    } else if (selectedProjectId) {
                        const selectedProject = projectsData.find(function (p) { return p.value === selectedProjectId; });
                        const projectRooms = selectedProject && selectedProject.rooms ? selectedProject.rooms : [];

                        if (projectRooms.length > 0) {
                            showRoomSelectModal(projectRooms).then(function (selectedRoomId) {
                                if (selectedRoomId === null) {
                                    // Пользователь отменил выбор комнаты — товар не добавляем.
                                    enableButton();
                                    return;
                                }
                                addProductToProject(productId, selectedProjectId, null, context, selectedRoomId);
                            });
                        } else {
                            addProductToProject(productId, selectedProjectId, null, context, null);
                        }
                    } else {
                        enableButton();
                    }
                });
            }
        });

        function addProductToProject(productId, projectId, projectName, context, roomId) {
            const quantity = parseInt($(context).find('input[name="quantity"]').val(), 10) || 1;
            const sizeIndex = $(context).find('select[name="size_index"]').val() || 0;

            $.ajax({
                url: '/api/project/add-product',
                method: 'POST',
                data: {
                    product_id: productId,
                    project_id: projectId,
                    new_project_name: projectName,
                    quantity,
                    size_index: sizeIndex,
                    room_id: roomId,
                },
                headers: { 'X-CSRF-TOKEN': csrfToken },
                success(response) {
                    if (response.success) {
                        if (projectName && response.project_id) {
                            const newItem = {
                                value: response.project_id.toString(),
                                text: projectName,
                            };
                            projectsData.unshift(newItem);
                            if (window.userProjects) window.userProjects.unshift(newItem);
                        }
                        const targetProjectId = response.project_id || projectId;
                        showAddedToProjectModal(targetProjectId);
                    } else {
                        if (window.showToast) {
                            window.showToast('error', response.message || i18n.errorAddingProduct);
                        }
                        enableButton();
                    }
                },
                error(xhr) {
                    let message = i18n.errorOccurred;
                    if (xhr.responseJSON && xhr.responseJSON.message) {
                        message = xhr.responseJSON.message;
                    }
                    if (window.showToast) {
                        window.showToast('error', message);
                    } else {
                        alert(message);
                    }
                    enableButton();
                },
            });
        }

        function addProductToNewProject(productId, projectName, context) {
            addProductToProject(productId, 'new', projectName, context);
        }

        function showRoomSelectModal(rooms) {
            return new Promise(function (resolve) {
                if (!rooms || rooms.length === 0) {
                    if (window.showToast) {
                        window.showToast('warning', i18n.noRoomsAvailable);
                    }
                    resolve(null);
                    return;
                }

                const roomItems = rooms.map(function (room) {
                    return {
                        value: room.id.toString(),
                        text: room.name || 'Room #' + room.id,
                    };
                });

                window.showSelectModal({
                    title: i18n.selectRoom,
                    placeholder: '',
                    items: roomItems,
                    confirmText: i18n.confirm,
                    cancelText: i18n.cancel,
                }).then(function (selectedRoomId) {
                    resolve(selectedRoomId || null);
                }).catch(function () {
                    resolve(null);
                });
            });
        }

        function addProductToRequest(productId, requestId, roomId, quantity, sizeIndex) {
            $.ajax({
                url: '/manager/request/product/add',
                method: 'POST',
                data: {
                    _token: csrfToken,
                    product_id: productId,
                    request_id: requestId,
                    room_id: roomId,
                    quantity,
                    size_index: sizeIndex,
                },
                success(response) {
                    if (response.success) {
                        if (window.showToast) {
                            window.showToast('success', response.message || i18n.productAddedToRequest);
                        } else {
                            alert(response.message || i18n.productAddedToRequest);
                        }
                        if (typeof $.fancybox !== 'undefined') $.fancybox.close();
                        setTimeout(function () { location.reload(); }, 1000);
                    } else {
                        if (window.showToast) {
                            window.showToast('error', response.message || i18n.errorAddingProduct);
                        }
                        if (enableButton) enableButton();
                    }
                },
                error(xhr) {
                    let message = i18n.errorOccurred;
                    if (xhr.responseJSON && xhr.responseJSON.message) message = xhr.responseJSON.message;
                    if (window.showToast) {
                        window.showToast('error', message);
                    } else {
                        alert(message);
                    }
                    if (enableButton) enableButton();
                },
            });
        }

        function addProductToProjectWithRoom(productId, projectId, roomId, quantity, sizeIndex) {
            $.ajax({
                url: '/addproducttospec',
                method: 'POST',
                data: {
                    _token: csrfToken,
                    product_id: productId,
                    project_id: projectId,
                    room_id: roomId,
                    product_qty: quantity,
                    size_index: sizeIndex,
                },
                success(response) {
                    if (response === 'added') {
                        if (typeof $.fancybox !== 'undefined') $.fancybox.close();
                        // Если пользователь УЖЕ в проекте (страница /project/{id}/…) —
                        // added-to-project-modal не нужна, достаточно закрыть продуктовую
                        // модалку и показать success toast. Иначе native <dialog> остаётся
                        // в top layer и перекрывает added-to-project-modal, кнопка зависает.
                        const inProjectContext = /\/project\/\d+(?:\/|$)/.test(location.pathname);
                        if (inProjectContext) {
                            if (window.ProductSelectionModal?.close) {
                                window.ProductSelectionModal.close();
                            }
                            if (window.showToast) {
                                window.showToast('success', i18n.productAddedToProject || 'Product added');
                            }
                            if (enableButton) enableButton();
                            // Перезагрузка чтобы обновить список товаров в текущей комнате/спеке.
                            setTimeout(() => window.location.reload(), 500);
                        } else {
                            showAddedToProjectModal(projectId);
                        }
                    } else {
                        if (window.showToast) {
                            window.showToast('error', i18n.errorAddingProduct);
                        } else {
                            alert(i18n.errorAddingProduct);
                        }
                        if (enableButton) enableButton();
                    }
                },
                error(xhr) {
                    let message = i18n.errorOccurred;
                    if (xhr.responseJSON && xhr.responseJSON.message) {
                        message = xhr.responseJSON.message;
                    } else if (xhr.responseJSON && xhr.responseJSON.error) {
                        message = xhr.responseJSON.error;
                    }
                    if (window.showToast) {
                        window.showToast('error', message);
                    } else {
                        alert(message);
                    }
                    if (enableButton) enableButton();
                },
            });
        }

        // ARK-2283: модалка "перейти в проект / остаться". Native <dialog> через
        // showModal() — попадает в top layer, единая стилистика с confirmation-dialog.
        function showAddedToProjectModal(targetProjectId) {
            const modal = document.querySelector('[data-added-to-project-modal]');
            if (!modal) {
                if (enableButton) enableButton();
                return;
            }

            const goLink = modal.querySelector('[data-go]');
            if (targetProjectId && goLink) {
                goLink.setAttribute('href', '/project/' + targetProjectId + '/spec');
            }

            function close() {
                if (typeof modal.close === 'function' && modal.open) modal.close();
                if (enableButton) enableButton();
            }

            modal.querySelectorAll('[data-stay]').forEach(function (el) {
                el.onclick = function (e) { e.preventDefault(); close(); };
            });
            modal.addEventListener('close', function () { if (enableButton) enableButton(); }, { once: true });

            if (typeof modal.showModal === 'function') modal.showModal();
        }
    };

    // Инициализация на странице товара (вне модалки) — ждёт DOMReady
    function autoInit() {
        const $btn = $('[data-add-to-project-btn]');
        const isInModal = $btn.closest('.product-detail-section').length > 0;
        if (!$btn.length || isInModal) return;

        const initial = window.productAddProjectInit;
        if (initial && initial.productId) {
            window.initAddToProjectButton(document, initial.productId, window.userProjects);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => $(autoInit));
    } else {
        $(autoInit);
    }
}

setupGlobalHelper();
